import React, { useEffect, useRef, useState } from "react";

/**
 * Homepage hero video (31 Oct 2026) — cinematic loop generated in Higgsfield from the
 * official Bay Views render, encoded by scripts/encode_hero_video.sh.
 *
 * Speed rules:
 *  - The still render (passed as children) is the LCP element and paints first, unchanged.
 *  - The <video> is NOT in the initial HTML: it mounts after window `load` + idle time,
 *    so it never competes with the first paint.
 *  - Phones get the 720p file, larger screens 1080p; WebM (VP9) first, MP4 fallback.
 *  - Skipped for Save-Data, slow 2G/3G connections and prefers-reduced-motion.
 *  - Fades in only once it is actually playing, so there is never a black flash.
 */
export default function HeroVideo({ name, children }) {
  const [mount, setMount] = useState(false);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const c = navigator.connection || {};
    if (c.saveData || /(^|-)2g$/.test(c.effectiveType || "")) return undefined; // 3g estimates are common on UAE mobile — don't skip
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let cancelled = false;
    const go = () => {
      if (cancelled) return;
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
      idle(() => !cancelled && setMount(true), { timeout: 2500 });
    };
    if (document.readyState === "complete") go();
    else window.addEventListener("load", go, { once: true });
    return () => { cancelled = true; window.removeEventListener("load", go); };
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!mount || !v) return;
    // iOS Safari only autoplays if the muted/playsinline ATTRIBUTES exist — React sets the
    // `muted` property but never writes the attribute, so set both explicitly before play().
    v.muted = true; v.defaultMuted = true;
    v.setAttribute("muted", ""); v.setAttribute("playsinline", ""); v.setAttribute("webkit-playsinline", "");
    const tryPlay = () => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    // Low-power mode blocks autoplay until the first touch — retry once on any interaction.
    const onTouch = () => tryPlay();
    window.addEventListener("touchstart", onTouch, { once: true, passive: true });
    return () => window.removeEventListener("touchstart", onTouch);
  }, [mount]);

  const small = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 820px)").matches;
  const h = small ? 720 : 1080;
  const base = `/videos/hero/${name}-${h}`;

  return (
    <>
      {children}
      {mount && (
        <video
          ref={ref}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-out"
          style={{ opacity: playing ? 1 : 0 }}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={() => setPlaying(true)}
          data-testid="hero-video"
        >
          {/* MP4 (H.264) first — iPhones cannot play VP9 WebM reliably; others fall through fine */}
          <source src={`${base}.mp4`} type="video/mp4" />
          <source src={`${base}.webm`} type="video/webm" />
        </video>
      )}
    </>
  );
}
